import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Navigate, useSearchParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import { can, CAP } from '@/lib/can'
import { cn } from '@/lib/cn'
import {
  useCreateTransportRef,
  useDeleteTransportRef,
  useSetTransportRefActive,
  useTransportRefs,
  useUpdateTransportRef,
} from '@/features/directories/api/useDirectory'
import {
  referencedMessage,
  referencedRefusal,
} from '@/features/directories/directoryText'
import {
  TransportFormSheet,
  type TransportDraft,
} from '@/features/directories/components/DirectoryFormSheets'
import type { TransportRef } from '@/types/domain'

/* Editing state.
 *
 * `null` means the sheet is closed. A record means edit; `'new'` means add. One
 * union rather than an `open` boolean beside a `record`, because those two can
 * disagree — an open sheet with no record and no add intent has no title to
 * render, and that state should not be representable.
 */
type EditTarget<T> = T | 'new' | null

/** The record itself, or undefined when adding. What the sheet takes. */
function editing<T>(target: EditTarget<T>): T | undefined {
  return target === null || target === 'new' ? undefined : target
}

/* Directories: the reference lists every case joins to. Clients have a section
 * of their own since the clients page, the account list moved to Settings
 * beside the roles (S-7), and the participants became «Контрагенты», a section
 * of their own with a category per record (S-22b) — so what stays here is the
 * transport catalogue, and the page is that one panel rather than a tab bar
 * with one tab in it. */

/** One catalogue row's label, code and inactive tag — the same text whether
 *  the row is a control (a manager) or read-only (everyone else). */
function TransportRowText({ row }: { row: TransportRef }) {
  const { t } = useTranslation()
  return (
    <>
      <span className="text-text-heading text-[13px]">{row.label}</span>
      <span className="text-text-muted font-mono text-[12px]">{row.code}</span>
      <span className="flex-1" />
      {/* Deactivation hides an option going FORWARD: the entry vanishes from
          the chooser on a new case while records that already reference it
          keep displaying it (FR-43). */}
      {!row.isActive ? <Tag>{t('common.form.inactive')}</Tag> : null}
    </>
  )
}

function TransportPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useTransportRefs()
  /* Every write here — add, edit, (de)activate, delete — is guarded by
     `directory.transport.manage`, which no seeded role holds (#128, N-07).
     Without it the catalogue is read-only: no «Добавить», no row opening a
     sheet whose save is a 403, no activate or delete. The capability, never a
     role name; the server re-checks every one (AD-21). */
  const { data: session } = useSession()
  const canManage = can(session, CAP.directoryTransportManage)
  /* The kind is carried beside the target because "add" has no record to read
     it from: the same sheet serves both lists, and which list its button sat in
     is the only thing that says whether a new row is a mode or an equipment
     type. */
  const [target, setTarget] = useState<EditTarget<TransportRef>>(null)
  const [kind, setKind] = useState<TransportRef['kind']>('mode')

  const create = useCreateTransportRef()
  const update = useUpdateTransportRef()
  const setActive = useSetTransportRefActive()
  const remove = useDeleteTransportRef()

  /* The refusal, which this panel's own comment has described since Epic 7 while
     no control could produce it: `DELETE /directories/transport/:id` was mounted
     and `useDeleteTransportRef` had no consumer, so "a refused delete offers
     deactivation instead" documented a path nobody could take. */
  const refusal = referencedRefusal(remove.error)

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const modes = data.filter((item) => item.kind === 'mode')
  const equipment = data.filter((item) => item.kind === 'equipment')

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">
      {[
        { kind: 'mode' as const, title: t('directories.transport.modes'), rows: modes },
        {
          kind: 'equipment' as const,
          title: t('directories.transport.equipmentTypes'),
          rows: equipment,
        },
      ].map((group) => (
        <Card key={group.kind}>
          <CardHeader
            action={
              canManage ? (
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={Plus}
                  onClick={() => {
                    setKind(group.kind)
                    setTarget('new')
                  }}
                >
                  {t('directories.transport.add')}
                </Button>
              ) : undefined
            }
          >
            <CardTitle>{group.title}</CardTitle>
          </CardHeader>
          <ul className="flex flex-col px-[18px] pb-3">
            {group.rows.map((row, index) => (
              <li
                key={row.id}
                className={cn(
                  index !== group.rows.length - 1 && 'border-line-hair border-b',
                )}
              >
                {/* The row's actions are SIBLINGS of the row button, not inside
                    it: a button nested in a button is invalid, and the outer
                    one would swallow the click before either fired. */}
                <div className="flex items-center gap-1">
                  {canManage ? (
                    <>
                      {/* A button rather than a clickable li: this row is a list
                          item, not a table row, so the native control carries the
                          semantics and the keyboard behaviour without either being
                          reimplemented. */}
                      <button
                        type="button"
                        onClick={() => {
                          setKind(row.kind)
                          setTarget(row)
                        }}
                        className="hover:bg-surface-hover -mx-2 flex flex-1 cursor-pointer items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors"
                      >
                        <TransportRowText row={row} />
                      </button>

                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={setActive.isPending}
                        onClick={() =>
                          setActive.mutate({ id: row.id, isActive: !row.isActive })
                        }
                      >
                        {row.isActive
                          ? t('directories.deactivate')
                          : t('directories.activate')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(row.id)}
                      >
                        {t('directories.transport.delete')}
                      </Button>
                    </>
                  ) : (
                    /* A reader who may not manage the catalogue reads it: the
                       row is text, not a control that opens a sheet. */
                    <div className="-mx-2 flex flex-1 items-center gap-3 px-2 py-2.5">
                      <TransportRowText row={row} />
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {/* A refused delete offers deactivation instead (FR-43, Story 7.4 AC 5),
          and the message names what still references the entry.

          NO merge action beside it, unlike the client and counterparty pages: a
          transport mode has nothing to merge into, which is why the server sends
          `deactivate` as its suggestion. The deactivate control is already on
          every row, so the message points at something the reader can see. */}
      {refusal ? (
        <div className="lg:col-span-2">
          <Banner tone="attention" title={t('directories.transport.delete')}>
            {referencedMessage(refusal, t)}
          </Banner>
        </div>
      ) : null}

      {[create, update, setActive].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="lg:col-span-2">
            <ErrorState error={mutation.error} onRetry={() => mutation.reset()} />
          </div>
        ) : null,
      )}

      {/* One sheet for both lists — only one can be open at a time. Not
          mounted for a reader who may not manage the catalogue. */}
      {canManage ? (
        <TransportFormSheet
          open={target !== null}
          onClose={() => setTarget(null)}
          record={editing(target)}
          kind={kind}
          pending={create.isPending || update.isPending}
          onSubmit={(values: TransportDraft) => {
            const record = editing(target)
            const payload = {
              // The kind is fixed at creation: moving an entry between the two
              // lists would silently change what every case referencing it means.
              kind: record?.kind ?? kind,
              label: values.label,
              code: values.code,
              sortOrder: record?.sortOrder ?? 0,
            }

            if (record) {
              // Activation is its own endpoint, so the two are separate calls
              // rather than one PUT that quietly does both.
              update.mutate({ id: record.id, payload }, {
                onSuccess: () => {
                  if (values.active !== record.isActive) {
                    setActive.mutate({ id: record.id, isActive: values.active })
                  }
                  setTarget(null)
                },
              })
              return
            }
            create.mutate(payload, { onSuccess: () => setTarget(null) })
          }}
        />
      ) : null}
    </div>
  )
}

export function DirectoriesPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const requestedClient = searchParams.get('client')

  /* Clients moved to their own section. The two addresses that used to
     reach them here — the tab, and global search's `?client=` — keep
     working as redirects, because both live in bookmarks and chats. */
  if (requestedClient) {
    return <Navigate to={`/clients/${encodeURIComponent(requestedClient)}`} replace />
  }
  if (requested === 'clients') {
    return <Navigate to="/clients" replace />
  }
  /* The account list moved to Settings beside the roles (S-7) — the old tab
     keeps landing there. Settings decides whether this reader may see it. */
  if (requested === 'users') {
    return <Navigate to="/settings?tab=users" replace />
  }
  /* The participants became counterparties (S-22b). The section decides
     whether this reader may see it. */
  if (requested === 'participants') {
    return <Navigate to="/counterparties" replace />
  }

  return (
    <>
      <PageHeader
        title={t('directories.title')}
        subtitle={t('directories.subtitle')}
      />

      <TransportPanel />
    </>
  )
}
